import React, { useState, useEffect, useCallback, useRef } from 'react';

const SOURCE_TYPE_COLORS = {
  paid: { bg: '#FEE2E2', color: '#EF4444', label: 'Paid' },
  organic_search: { bg: '#D1FAE5', color: '#10B981', label: 'Organic Search' },
  ai_search: { bg: '#EDE9FE', color: '#8B5CF6', label: 'AI Search' },
  social: { bg: '#FEF3C7', color: '#F59E0B', label: 'Social' },
  email: { bg: '#DBEAFE', color: '#3B82F6', label: 'Email' },
  referral: { bg: '#CFFAFE', color: '#06B6D4', label: 'Referral' },
  direct: { bg: '#F3F4F6', color: '#6B7280', label: 'Direct' },
};

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toISOString().split('T')[0];
};

const formatDisplayDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const pct = (n, total) => total > 0 ? ((n / total) * 100).toFixed(1) : '0.0';

const convColor = (rate) => {
  const r = parseFloat(rate);
  if (r >= 3) return '#10B981';
  if (r >= 1) return '#F59E0B';
  return '#EF4444';
};

const getTypeBadge = (type) => {
  const t = SOURCE_TYPE_COLORS[type] || SOURCE_TYPE_COLORS.direct;
  return { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: t.bg, color: t.color };
};

const getTypeLabel = (type) => {
  const t = SOURCE_TYPE_COLORS[type];
  return t ? t.label : type || 'Unknown';
};

const SourceAnalytics = ({ getAuthHeaders, handleLogout }) => {
  const [subTab, setSubTab] = useState('web');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Date range - default last 30 days
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return formatDate(d);
  });
  const [endDate, setEndDate] = useState(() => formatDate(new Date()));

  // Web sources state
  const [webData, setWebData] = useState(null);
  const [webLoading, setWebLoading] = useState(false);
  const [webError, setWebError] = useState('');

  // Card sources state
  const [cardData, setCardData] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState('');

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const apiFetch = useCallback(async (url) => {
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (response.status === 401) { handleLogout(); return null; }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }, [getAuthHeaders, handleLogout]);

  const fetchWebData = useCallback(async () => {
    setWebLoading(true);
    setWebError('');
    try {
      const data = await apiFetch(`/api/visitors/admin/web-source?startDate=${startDate}&endDate=${endDate}`);
      if (data) setWebData(data);
    } catch (err) {
      setWebError(err.message || 'Failed to fetch web source data');
    } finally {
      setWebLoading(false);
    }
  }, [apiFetch, startDate, endDate]);

  const fetchCardData = useCallback(async () => {
    setCardLoading(true);
    setCardError('');
    try {
      const data = await apiFetch(`/api/visitors/admin/card-source?startDate=${startDate}&endDate=${endDate}`);
      if (data) setCardData(data);
    } catch (err) {
      setCardError(err.message || 'Failed to fetch card source data');
    } finally {
      setCardLoading(false);
    }
  }, [apiFetch, startDate, endDate]);

  // Fetch on tab change or date change
  useEffect(() => {
    if (subTab === 'web') fetchWebData();
    else fetchCardData();
  }, [subTab, startDate, endDate]);

  // Auto-refresh logic
  useEffect(() => {
    if (autoRefresh) {
      setCountdown(60);
      countdownRef.current = setInterval(() => setCountdown(c => c - 1), 1000);
      timerRef.current = setInterval(() => {
        setCountdown(60);
        if (subTab === 'web') fetchWebData();
        else fetchCardData();
      }, 60000);
    }
    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [autoRefresh, subTab, fetchWebData, fetchCardData]);

  // CSV export for platform table
  const exportCSV = (rows, filename) => {
    if (!rows || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ========== STYLES ==========
  const s = {
    subTabs: { display: 'flex', gap: 4, marginBottom: 24, background: '#f1f5f9', borderRadius: 10, padding: 4 },
    subTab: { padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, background: 'transparent', color: '#475569', transition: 'all 0.2s' },
    subTabActive: { background: 'white', color: '#2055df', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    card: { background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 20 },
    cardHeader: { padding: '18px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
    cardTitle: { fontSize: '1.1rem', fontWeight: 600, color: '#0f172a', margin: 0 },
    cardBody: { padding: 24 },
    statsGrid: { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
    statCard: { background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 20 },
    statLabel: { fontSize: 13, color: '#64748b', marginBottom: 6, fontWeight: 500 },
    statValue: { fontSize: '1.6rem', fontWeight: 700, color: '#0f172a' },
    statSub: { fontSize: 12, color: '#94a3b8', marginTop: 4 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '12px 16px', background: '#f8fafc', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' },
    td: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontSize: 14, color: '#334155' },
    dateRow: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' },
    dateInput: { padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, color: '#334155', background: 'white' },
    btn: { padding: '10px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
    btnPrimary: { background: '#2055df', color: 'white' },
    btnSuccess: { background: '#10b981', color: 'white' },
    btnOutline: { background: 'white', color: '#475569', border: '1px solid #e2e8f0' },
    empty: { textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 15 },
    loading: { textAlign: 'center', padding: 48, color: '#64748b' },
    error: { background: '#FEF2F2', color: '#DC2626', padding: 16, borderRadius: 8, marginBottom: 16, fontSize: 14 },
    barContainer: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
    barLabel: { minWidth: isMobile ? 80 : 120, fontSize: 14, color: '#334155', fontWeight: 500 },
    barTrack: { flex: 1, height: 24, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden', position: 'relative' },
    barValue: { fontSize: 12, minWidth: 50, textAlign: 'right', color: '#64748b', fontWeight: 600 },
    refreshRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b' },
    overflowX: { overflowX: 'auto' },
  };

  // ========== SHARED UI HELPERS ==========
  const renderBar = (label, value, total, color) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return (
      <div key={label} style={s.barContainer}>
        <span style={s.barLabel}>{label}</span>
        <div style={s.barTrack}>
          <div style={{ height: '100%', width: `${Math.max(percentage, 0.5)}%`, background: color, borderRadius: 6, transition: 'width 0.5s ease', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: percentage > 15 ? 8 : 0 }}>
            {percentage > 15 && <span style={{ fontSize: 11, color: 'white', fontWeight: 600 }}>{percentage.toFixed(1)}%</span>}
          </div>
        </div>
        <span style={s.barValue}>{value.toLocaleString()} ({percentage.toFixed(1)}%)</span>
      </div>
    );
  };

  const renderConversionBar = (rate) => {
    const r = parseFloat(rate) || 0;
    const color = convColor(r);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', minWidth: 60 }}>
          <div style={{ height: '100%', width: `${Math.min(r * 10, 100)}%`, background: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color, minWidth: 42 }}>{r.toFixed(2)}%</span>
      </div>
    );
  };

  const renderDateControls = () => (
    <div style={s.dateRow}>
      <label style={{ fontSize: 14, color: '#64748b', fontWeight: 500 }}>From</label>
      <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={s.dateInput} />
      <label style={{ fontSize: 14, color: '#64748b', fontWeight: 500 }}>To</label>
      <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={s.dateInput} />
      <button onClick={() => { if (subTab === 'web') fetchWebData(); else fetchCardData(); }} style={{ ...s.btn, ...s.btnPrimary }}>
        Refresh
      </button>
      <div style={{ ...s.refreshRow, marginLeft: 'auto' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
          <span>Auto-refresh</span>
        </label>
        {autoRefresh && <span style={{ fontSize: 12, background: '#f1f5f9', padding: '2px 8px', borderRadius: 4 }}>{countdown}s</span>}
      </div>
    </div>
  );

  const renderLoading = () => (
    <div style={s.loading}>
      <div style={{ fontSize: 28, marginBottom: 12 }}>Loading...</div>
      <div>Fetching source analytics data</div>
    </div>
  );

  const renderError = (msg) => (
    <div style={s.error}>Failed to load data: {msg}</div>
  );

  const renderEmpty = (msg) => (
    <div style={s.empty}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>No Data</div>
      <div>{msg || 'No data found for the selected date range.'}</div>
    </div>
  );

  // ========== WEB SOURCES TAB ==========
  const renderWebSources = () => {
    if (webLoading) return renderLoading();
    if (webError) return renderError(webError);
    if (!webData) return renderEmpty('Select a date range and click Refresh.');

    const summary = webData.summary || {};
    const sourceBreakdown = webData.sourceBreakdown || [];
    const platforms = webData.platforms || [];
    const landingPages = webData.landingPages || [];
    const devices = webData.devices || [];
    const browsers = webData.browsers || [];
    const dailyTrend = webData.dailyTrend || [];
    const totalVisitors = summary.totalVisitors || 0;

    const statCards = [
      { label: 'Total Visitors', value: totalVisitors, sub: '' },
      { label: 'Paid', value: summary.paid || 0, sub: `${pct(summary.paid || 0, totalVisitors)}%`, color: '#EF4444' },
      { label: 'Organic Search', value: summary.organicSearch || 0, sub: `${pct(summary.organicSearch || 0, totalVisitors)}%`, color: '#10B981' },
      { label: 'AI Search', value: summary.aiSearch || 0, sub: `${pct(summary.aiSearch || 0, totalVisitors)}%`, color: '#8B5CF6' },
      { label: 'Social', value: summary.social || 0, sub: `${pct(summary.social || 0, totalVisitors)}%`, color: '#F59E0B' },
      { label: 'Direct', value: summary.direct || 0, sub: `${pct(summary.direct || 0, totalVisitors)}%`, color: '#6B7280' },
      { label: 'Referral', value: summary.referral || 0, sub: `${pct(summary.referral || 0, totalVisitors)}%`, color: '#06B6D4' },
      { label: 'Email', value: summary.email || 0, sub: `${pct(summary.email || 0, totalVisitors)}%`, color: '#3B82F6' },
    ];

    return (
      <div>
        {/* Summary Cards */}
        <div style={s.statsGrid}>
          {statCards.map((sc, i) => (
            <div key={i} style={{ ...s.statCard, borderLeft: sc.color ? `4px solid ${sc.color}` : `4px solid #2055df` }}>
              <div style={s.statLabel}>{sc.label}</div>
              <div style={s.statValue}>{(sc.value || 0).toLocaleString()}</div>
              {sc.sub && <div style={{ ...s.statSub, color: sc.color || '#64748b' }}>Conv: {summary[`${sc.label.replace(/ /g, '').charAt(0).toLowerCase() + sc.label.replace(/ /g, '').slice(1)}ConvRate`] || summary.conversionRate || 'N/A'}</div>}
            </div>
          ))}
        </div>

        {/* Source Type Breakdown */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>Source Type Breakdown</h3>
          </div>
          <div style={s.cardBody}>
            {sourceBreakdown.length === 0 ? (
              <div style={s.empty}>No source breakdown data</div>
            ) : (
              sourceBreakdown.map(item => {
                const typeInfo = SOURCE_TYPE_COLORS[item.type] || SOURCE_TYPE_COLORS.direct;
                return renderBar(getTypeLabel(item.type), item.visitors || item.count || 0, totalVisitors, typeInfo.color);
              })
            )}
          </div>
        </div>

        {/* Platform Performance Table */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>Platform Performance</h3>
            <button
              onClick={() => {
                const rows = platforms.map(p => ({
                  Platform: p.platform || p.name,
                  Type: getTypeLabel(p.type),
                  Visitors: p.visitors || 0,
                  Conversions: p.conversions || 0,
                  'Conversion Rate': `${(p.conversionRate || 0).toFixed(2)}%`
                }));
                exportCSV(rows, 'platform-performance');
              }}
              style={{ ...s.btn, ...s.btnSuccess, fontSize: 13 }}
            >
              Export CSV
            </button>
          </div>
          <div style={s.overflowX}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Platform</th>
                  <th style={s.th}>Type</th>
                  <th style={s.th}>Visitors</th>
                  <th style={s.th}>Conversions</th>
                  <th style={{ ...s.th, minWidth: 180 }}>Conversion Rate</th>
                </tr>
              </thead>
              <tbody>
                {platforms.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', color: '#94a3b8' }}>No platform data</td></tr>
                ) : (
                  platforms.sort((a, b) => (b.visitors || 0) - (a.visitors || 0)).map((p, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                      <td style={{ ...s.td, fontWeight: 600 }}>{p.platform || p.name || 'Unknown'}</td>
                      <td style={s.td}><span style={getTypeBadge(p.type)}>{getTypeLabel(p.type)}</span></td>
                      <td style={s.td}>{(p.visitors || 0).toLocaleString()}</td>
                      <td style={s.td}>{(p.conversions || 0).toLocaleString()}</td>
                      <td style={s.td}>{renderConversionBar(p.conversionRate || 0)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Landing Pages */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>Top Landing Pages</h3>
          </div>
          <div style={s.overflowX}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Page</th>
                  <th style={s.th}>Visitors</th>
                  <th style={s.th}>Conversions</th>
                  <th style={{ ...s.th, minWidth: 160 }}>Conversion Rate</th>
                </tr>
              </thead>
              <tbody>
                {landingPages.length === 0 ? (
                  <tr><td colSpan={4} style={{ ...s.td, textAlign: 'center', color: '#94a3b8' }}>No landing page data</td></tr>
                ) : (
                  landingPages.map((lp, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                      <td style={{ ...s.td, fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lp.page || lp.path || '/'}</td>
                      <td style={s.td}>{(lp.visitors || 0).toLocaleString()}</td>
                      <td style={s.td}>{(lp.conversions || 0).toLocaleString()}</td>
                      <td style={s.td}>{renderConversionBar(lp.conversionRate || 0)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Device & Browser Breakdown side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20, marginBottom: 20 }}>
          {/* Device Breakdown */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <h3 style={s.cardTitle}>Device Breakdown</h3>
            </div>
            <div style={s.cardBody}>
              {devices.length === 0 ? (
                <div style={{ ...s.empty, padding: 24 }}>No device data</div>
              ) : (
                devices.map(d => {
                  const colors = { desktop: '#2055df', mobile: '#8B5CF6', tablet: '#F59E0B' };
                  return renderBar(d.device || d.name || 'Unknown', d.count || d.visitors || 0, totalVisitors, colors[(d.device || d.name || '').toLowerCase()] || '#6B7280');
                })
              )}
            </div>
          </div>

          {/* Browser Breakdown */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <h3 style={s.cardTitle}>Browser Breakdown</h3>
            </div>
            <div style={s.cardBody}>
              {browsers.length === 0 ? (
                <div style={{ ...s.empty, padding: 24 }}>No browser data</div>
              ) : (
                browsers.map(b => {
                  const colors = { chrome: '#4285F4', safari: '#000000', firefox: '#FF7139', edge: '#0078D7', opera: '#FF1B2D' };
                  return renderBar(b.browser || b.name || 'Unknown', b.count || b.visitors || 0, totalVisitors, colors[(b.browser || b.name || '').toLowerCase()] || '#6B7280');
                })
              )}
            </div>
          </div>
        </div>

        {/* Daily Trend Table */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>Daily Trend</h3>
          </div>
          <div style={s.overflowX}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Date</th>
                  <th style={s.th}>Total</th>
                  <th style={s.th}>Paid</th>
                  <th style={s.th}>Organic</th>
                  <th style={s.th}>AI Search</th>
                  <th style={s.th}>Social</th>
                  <th style={s.th}>Direct</th>
                  <th style={s.th}>Referral</th>
                  <th style={s.th}>Email</th>
                </tr>
              </thead>
              <tbody>
                {dailyTrend.length === 0 ? (
                  <tr><td colSpan={9} style={{ ...s.td, textAlign: 'center', color: '#94a3b8' }}>No trend data</td></tr>
                ) : (
                  dailyTrend.map((day, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                      <td style={{ ...s.td, fontWeight: 500 }}>{formatDisplayDate(day.date)}</td>
                      <td style={{ ...s.td, fontWeight: 700 }}>{(day.total || 0).toLocaleString()}</td>
                      <td style={s.td}>{day.paid || 0}</td>
                      <td style={s.td}>{day.organicSearch || day.organic || 0}</td>
                      <td style={s.td}>{day.aiSearch || 0}</td>
                      <td style={s.td}>{day.social || 0}</td>
                      <td style={s.td}>{day.direct || 0}</td>
                      <td style={s.td}>{day.referral || 0}</td>
                      <td style={s.td}>{day.email || 0}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ========== CARD SOURCES TAB ==========
  const renderCardSources = () => {
    if (cardLoading) return renderLoading();
    if (cardError) return renderError(cardError);
    if (!cardData) return renderEmpty('Select a date range and click Refresh.');

    const summary = cardData.summary || {};
    const sourceBreakdown = cardData.sourceBreakdown || [];
    const platforms = cardData.platforms || [];
    const campaigns = cardData.campaigns || [];
    const landingPages = cardData.landingPages || [];
    const recentRegistrations = cardData.recentRegistrations || [];
    const totalRegistrations = summary.totalRegistrations || 0;

    const statCards = [
      { label: 'Total Registrations', value: totalRegistrations, color: '#2055df' },
      { label: 'Paid', value: summary.paid || 0, color: '#EF4444' },
      { label: 'Organic Search', value: summary.organicSearch || 0, color: '#10B981' },
      { label: 'AI Search', value: summary.aiSearch || 0, color: '#8B5CF6' },
      { label: 'Social', value: summary.social || 0, color: '#F59E0B' },
      { label: 'Direct', value: summary.direct || 0, color: '#6B7280' },
      { label: 'Referral', value: summary.referral || 0, color: '#06B6D4' },
      { label: 'Email', value: summary.email || 0, color: '#3B82F6' },
    ];

    return (
      <div>
        {/* Summary Cards */}
        <div style={s.statsGrid}>
          {statCards.map((sc, i) => (
            <div key={i} style={{ ...s.statCard, borderLeft: `4px solid ${sc.color}` }}>
              <div style={s.statLabel}>{sc.label}</div>
              <div style={s.statValue}>{(sc.value || 0).toLocaleString()}</div>
              {totalRegistrations > 0 && i > 0 && (
                <div style={{ ...s.statSub, color: sc.color }}>{pct(sc.value || 0, totalRegistrations)}% of total</div>
              )}
            </div>
          ))}
        </div>

        {/* Source Type Breakdown */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>Source Type Breakdown</h3>
          </div>
          <div style={s.cardBody}>
            {sourceBreakdown.length === 0 ? (
              <div style={s.empty}>No source breakdown data</div>
            ) : (
              sourceBreakdown.map(item => {
                const typeInfo = SOURCE_TYPE_COLORS[item.type] || SOURCE_TYPE_COLORS.direct;
                return renderBar(getTypeLabel(item.type), item.registrations || item.count || 0, totalRegistrations, typeInfo.color);
              })
            )}
          </div>
        </div>

        {/* Platform Performance Table */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>Platform Performance</h3>
            <button
              onClick={() => {
                const rows = platforms.map(p => ({
                  Platform: p.platform || p.name,
                  Type: getTypeLabel(p.type),
                  Registrations: p.registrations || 0,
                  Percentage: `${pct(p.registrations || 0, totalRegistrations)}%`
                }));
                exportCSV(rows, 'card-platform-performance');
              }}
              style={{ ...s.btn, ...s.btnSuccess, fontSize: 13 }}
            >
              Export CSV
            </button>
          </div>
          <div style={s.overflowX}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Platform</th>
                  <th style={s.th}>Type</th>
                  <th style={s.th}>Registrations</th>
                  <th style={{ ...s.th, minWidth: 160 }}>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {platforms.length === 0 ? (
                  <tr><td colSpan={4} style={{ ...s.td, textAlign: 'center', color: '#94a3b8' }}>No platform data</td></tr>
                ) : (
                  platforms.sort((a, b) => (b.registrations || 0) - (a.registrations || 0)).map((p, i) => {
                    const percentage = totalRegistrations > 0 ? ((p.registrations || 0) / totalRegistrations) * 100 : 0;
                    const typeInfo = SOURCE_TYPE_COLORS[p.type] || SOURCE_TYPE_COLORS.direct;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                        <td style={{ ...s.td, fontWeight: 600 }}>{p.platform || p.name || 'Unknown'}</td>
                        <td style={s.td}><span style={getTypeBadge(p.type)}>{getTypeLabel(p.type)}</span></td>
                        <td style={s.td}>{(p.registrations || 0).toLocaleString()}</td>
                        <td style={s.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${percentage}%`, background: typeInfo.color, borderRadius: 4, transition: 'width 0.4s ease' }} />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b', minWidth: 42 }}>{percentage.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Campaigns Table */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>Top Campaigns</h3>
          </div>
          <div style={s.overflowX}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Campaign</th>
                  <th style={s.th}>Source</th>
                  <th style={s.th}>Registrations</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.length === 0 ? (
                  <tr><td colSpan={3} style={{ ...s.td, textAlign: 'center', color: '#94a3b8' }}>No campaign data</td></tr>
                ) : (
                  campaigns.map((c, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                      <td style={{ ...s.td, fontWeight: 600 }}>{c.campaign || c.name || 'N/A'}</td>
                      <td style={s.td}>{c.source || 'N/A'}</td>
                      <td style={s.td}>{(c.registrations || c.count || 0).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Landing Pages */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>Top Landing Pages</h3>
          </div>
          <div style={s.overflowX}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Page</th>
                  <th style={s.th}>Registrations</th>
                </tr>
              </thead>
              <tbody>
                {landingPages.length === 0 ? (
                  <tr><td colSpan={2} style={{ ...s.td, textAlign: 'center', color: '#94a3b8' }}>No landing page data</td></tr>
                ) : (
                  landingPages.map((lp, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                      <td style={{ ...s.td, fontWeight: 500, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lp.page || lp.path || '/'}</td>
                      <td style={s.td}>{(lp.registrations || lp.count || 0).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Registrations */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>Recent Registrations</h3>
          </div>
          <div style={s.overflowX}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Date</th>
                  <th style={s.th}>Source</th>
                  <th style={s.th}>Campaign</th>
                  <th style={s.th}>Landing Page</th>
                </tr>
              </thead>
              <tbody>
                {recentRegistrations.length === 0 ? (
                  <tr><td colSpan={4} style={{ ...s.td, textAlign: 'center', color: '#94a3b8' }}>No recent registrations</td></tr>
                ) : (
                  recentRegistrations.map((reg, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                      <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{formatDisplayDate(reg.date || reg.createdAt)}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={getTypeBadge(reg.type || reg.sourceType)}>{getTypeLabel(reg.type || reg.sourceType)}</span>
                          {(reg.platform || reg.sourcePlatform) && <span style={{ fontSize: 13, color: '#64748b' }}>{reg.platform || reg.sourcePlatform}</span>}
                        </div>
                      </td>
                      <td style={{ ...s.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reg.campaign || 'N/A'}</td>
                      <td style={{ ...s.td, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reg.landingPage || reg.page || 'N/A'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ========== MAIN RENDER ==========
  return (
    <div>
      {/* Sub-tabs */}
      <div style={s.subTabs}>
        {[
          { id: 'web', label: 'Web Sources' },
          { id: 'card', label: 'Card Sources' },
        ].map(tab => (
          <button
            key={tab.id}
            style={{ ...s.subTab, ...(subTab === tab.id ? s.subTabActive : {}) }}
            onClick={() => setSubTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Date range controls */}
      {renderDateControls()}

      {/* Tab content */}
      {subTab === 'web' ? renderWebSources() : renderCardSources()}
    </div>
  );
};

export default SourceAnalytics;
